import { useQuery } from '@tanstack/react-query';
import type { AuditTimelineEntry } from '../../types/auditTimeline';
import { getRowAuditLog } from './api';

// Thin React Query wrapper for the row audit endpoint (P2b-2 §7.5). The
// component that renders the timeline (AuditTimeline) is presentational
// by design — the hook lives next to it so callers can compose:
//
//   const audit = useRowAuditLog(rolloutId, rowId);
//   <AuditTimeline entries={audit.data ?? []} isLoading={audit.isLoading}
//                  error={audit.error?.message ?? null} />
//
// When Campaign / Account timelines land in P4, those entities get their
// own hooks (useCampaignAuditLog, etc.) and reuse the same presentational
// AuditTimeline. Splitting fetch from render keeps the component free of
// entity-type switches.
//
// refetchOnWindowFocus: true mirrors the rest of the rollout queries —
// audit entries are append-only so a stale cache is harmless, but a
// fresh fetch on tab-return catches the most recent changes without
// requiring manual cache invalidation from every mutation site.

export type UseRowAuditLogResult = {
  data: AuditTimelineEntry[] | undefined;
  isLoading: boolean;
  error: string | null;
};

export function useRowAuditLog(
  rolloutId: string | null | undefined,
  rowId: string | null | undefined,
): UseRowAuditLogResult {
  const query = useQuery({
    queryKey: ['rollout', rolloutId, 'row', rowId, 'audit'],
    queryFn: () => getRowAuditLog(rolloutId!, rowId!),
    enabled: !!rolloutId && !!rowId,
    refetchOnWindowFocus: true,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}
